import React, { useState, useEffect } from 'react';
import { Lock, ThumbsUp, ThumbsDown } from 'lucide-react';
import styles from './form.module.css';

const DebateForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const allowArgument = content?.settings?.allowArgument !== false;
    const maxChars = Number(content?.settings?.maxChars ?? 300);

    const [side, setSide] = useState(() => userAnswer?.side || null);
    const [argument, setArgument] = useState(() => userAnswer?.argument || '');

    useEffect(() => {
        setSide(userAnswer?.side || null);
        setArgument(userAnswer?.argument || '');
    }, [userAnswer]);

    const hasSubmitted = userAnswer != null;
    const isDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted && side === userAnswer.side && argument === (userAnswer.argument || '');
    const submitDisabled = isPreview || isLocked || !side || (hasSubmitted && (!allowUpdateResponse || isUnchanged));
    const submitLabel = !hasSubmitted ? (isLocked ? 'Voting Closed' : 'Submit') : (!allowUpdateResponse || isUnchanged ? 'Submitted ✓' : 'Update response');

    const handleSubmit = () => {
        if (submitDisabled) return;
        const payload = { side };
        if (allowArgument && argument.trim()) payload.argument = argument.trim();
        onSubmit(payload);
    };

    const sideConfig = {
        agree: { label: content?.settings?.agreeLabel || 'Agree', Icon: ThumbsUp, variant: 'positive' },
        disagree: { label: content?.settings?.disagreeLabel || 'Disagree', Icon: ThumbsDown, variant: 'negative' },
    };

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Do you agree or disagree?'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden="true" /> Voting Locked</span>}
            </div>
            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>{content.settings.descText}</div>
            )}
            <div className={styles.choiceCardRow}>
                {['agree', 'disagree'].map((s) => {
                    const cfg = sideConfig[s];
                    const isActive = side === s;
                    const Icon = cfg.Icon;
                    return (
                        <button
                            key={s}
                            type="button"
                            className={`${styles.choiceCard} ${isActive ? `${styles.active} ${styles[cfg.variant]}` : ''}`}
                            onClick={() => !isDisabled && setSide(s)}
                            disabled={isDisabled}
                            aria-pressed={isActive}
                        >
                            <span className={styles.choiceCardIcon}><Icon size={28} aria-hidden="true" /></span>
                            <span className={styles.choiceCardLabel}>{cfg.label}</span>
                        </button>
                    );
                })}
            </div>
            {allowArgument && side && !isDisabled && (
                <div className={styles.wordInputWrap}>
                    <label style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                        Add an argument <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
                    </label>
                    <textarea
                        className={styles.textArea}
                        value={argument}
                        onChange={(e) => setArgument(e.target.value.slice(0, maxChars))}
                        placeholder="Share your reasoning…"
                        rows={3}
                    />
                    <span className={styles.charCount}>{argument.length}/{maxChars}</span>
                </div>
            )}
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitDisabled}>
                {submitLabel}
            </button>
        </div>
    );
};

export default DebateForm;
